import { useEffect, useMemo, useState } from 'react';
import type { MenuProviderProps } from '@ariakit/react';
import { useMenuContext } from '@ariakit/react';
import { enableObservabilityDateRangePickerV2 } from '@gonfalon/dogfood-flags/dogfood-flags';
import { Button, Text as LPText } from '@launchpad-ui/components';
import { DatePickerStateProvider } from '@rehookify/datepicker';
import moment from 'moment';

import { Badge } from '../../Badge/Badge';
import { Box } from '../../Box/Box';
import { Form } from '../../Form/Form';
import { IconSolidCheck, IconSolidCheveronDown, IconSolidCheveronRight, IconSolidClock } from '../../icons';
import { LiveModeIndicator } from '../../LiveModeIndicator/LiveModeIndicator';
import type { MenuButtonProps } from '../../Menu/Menu';
import { Menu } from '../../Menu/Menu';
import { Stack } from '../../Stack/Stack';
import { Text } from '../../Text/Text';
import { DatePicker } from '../Calendar/DatePicker';
import { DateInput } from '../DateInput';
import { TimeInput } from '../TimeInput';

import { VALID_DATE_INPUT_FORMATS, VALID_TIME_INPUT_FORMATS } from './constants';
import { DateRangePickerV2 } from './DateRangePickerV2';
import {
  formatDisplayedDate,
  getInputLabel,
  getTimeInfo,
  getTimeStringFromDate,
  isCustomSelected,
  isPresetSelected,
  setTimeOnDate,
} from './helpers';
import type { DateRangePreset, DateRangeValue } from './types';
import { presetLabel, presetStartDate, presetValue } from './utils';

export type { DateRangePreset, DateRangeValue } from './types';

export enum MenuState {
  Default,
  Custom,
}

type Props = {
  selectedValue: DateRangeValue;
  onDatesChange: (startDate: Date, endDate: Date, preset?: DateRangePreset) => void;
  onLiveModeChange?: (isLiveMode: boolean) => void;
  presets: DateRangePreset[];
  minDate: Date;
  maxDate?: Date;
  noCustom?: boolean;
  defaultPreset?: DateRangePreset;
  placement?: MenuProviderProps['placement'];
  size?: 'small' | 'medium';
  variant?: 'minimal' | 'default';
  setDefaultPreset?: (preset: DateRangePreset) => void;
  showLiveMode?: boolean;
  liveModePaused?: boolean;
  disabled?: boolean;
  showTextInput?: boolean;
} & Omit<MenuButtonProps, 'ref' | 'store'>;

export const DateRangePicker: React.FC<Props> = (props) => {
  const useV2 = enableObservabilityDateRangePickerV2();
  const [open, setOpen] = useState(false);

  // If V2 is enabled, use the new React Aria-based implementation
  if (useV2) {
    // Filter out props that V2 doesn't support (these are MenuButton-specific props)
    const {
      size,
      variant,
      emphasis,
      kind,
      iconLeft,
      iconRight,
      icon,
      placement,
      defaultPreset,
      setDefaultPreset,
      ...v2Props
    } = props;

    return <DateRangePickerV2 {...v2Props} />;
  }

  // Otherwise, use the original implementation
  return (
    <Menu open={open} setOpen={setOpen} placement={props.placement ?? 'bottom-end'}>
      {/* Rendering inside wrapper so we can work with menu store via useMenu. */}
      <DateRangePickerImpl {...props} open={open} />
    </Menu>
  );
};

const CheckboxIconIfSelected = ({ isSelected, size = 16 }: { isSelected: boolean; size?: number }) => (
  <Box style={{ height: size, width: size }} color="primaryEnabled">
    {isSelected && <IconSolidCheck size={size} />}
  </Box>
);

const DateRangePickerImpl = ({
  selectedValue,
  onDatesChange,
  onLiveModeChange,
  presets,
  noCustom,
  defaultPreset,
  setDefaultPreset,
  minDate,
  maxDate = moment().toDate(),
  open,
  iconLeft,
  size = 'small',
  variant = 'default',
  showLiveMode = false,
  liveModePaused = false,
  ...props
}: Props & { open: boolean }) => {
  const [menuState, setMenuState] = useState<MenuState>(MenuState.Default);
  const [startDateIsValid, setStartDateIsValid] = useState<boolean>(true);
  const [endDateIsValid, setEndDateIsValid] = useState<boolean>(true);
  const [startTimeIsValid, setStartTimeIsValid] = useState<boolean>(true);
  const [endTimeIsValid, setEndTimeIsValid] = useState<boolean>(true);
  const useAbsoluteTime = !selectedValue.selectedPreset;
  const [absoluteDateRange, setAbsoluteDateRange] = useState<Date[]>(
    useAbsoluteTime ? [selectedValue.startDate, selectedValue.endDate] : [],
  );

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const menu = useMenuContext()!;

  useEffect(() => {
    if (!open) {
      setMenuState(MenuState.Default);
    }
  }, [open]);

  const startDatePlaceholder = useMemo(() => formatDisplayedDate(selectedValue.startDate), [selectedValue.startDate]);

  const endDatePlaceholder = useMemo(() => formatDisplayedDate(selectedValue.endDate), [selectedValue.endDate]);

  const startTimePlaceholder = useMemo(() => getTimeStringFromDate(selectedValue.startDate), [selectedValue.startDate]);

  const endTimePlaceholder = useMemo(() => getTimeStringFromDate(selectedValue.endDate, true), [selectedValue.endDate]);

  const [buttonLabel, setButtonLabel] = useState<string>(getInputLabel({ selectedValue, presets }));

  const handleDatesChange = ({ startDate, endDate, selectedPreset }: DateRangeValue) => {
    onDatesChange(startDate, endDate, selectedPreset);

    if (startDate && endDate) {
      menu.setOpen(false);
      setMenuState(MenuState.Default);
    }
  };

  const handleLiveModeToggle = () => {
    const newLiveModeState = !selectedValue.isLiveMode;

    // When live mode is enabled, set 15-minute preset as default range
    if (newLiveModeState) {
      const fifteenMinutePreset: DateRangePreset = { unit: 'minutes', quantity: 15 };
      const end = moment().toDate();
      const start = presetStartDate(fifteenMinutePreset);

      handleDatesChange({ startDate: start, endDate: end, selectedPreset: fifteenMinutePreset });
    }

    if (onLiveModeChange) {
      onLiveModeChange(newLiveModeState);
    }

    menu.setOpen(false);
  };

  const handleStartDateInput = (value: string) => {
    const validDateFormat = VALID_DATE_INPUT_FORMATS.find((format) => moment(value, format, true).isValid());

    setStartDateIsValid(!!validDateFormat);
    if (validDateFormat) {
      const newDate = moment(value, validDateFormat).toDate();
      const startDate = setTimeOnDate(
        newDate,
        selectedValue.startDate.getHours(),
        selectedValue.startDate.getMinutes(),
      );

      onDatesChange(startDate, selectedValue.endDate, undefined);
    }
  };

  const handleEndDateInput = (value: string) => {
    const validDateFormat = VALID_DATE_INPUT_FORMATS.find((format) => moment(value, format, true).isValid());

    setEndDateIsValid(!!validDateFormat);
    if (validDateFormat) {
      const newDate = moment(value, validDateFormat).toDate();
      const endDate = setTimeOnDate(newDate, selectedValue.endDate.getHours(), selectedValue.endDate.getMinutes());

      onDatesChange(selectedValue.startDate, endDate, undefined);
    }
  };

  const handleStartTimeChange = (value: string) => {
    const isValidTimeInput = VALID_TIME_INPUT_FORMATS.map((format) => moment(value, format, true).isValid()).some(
      (isValid) => isValid,
    );

    setStartTimeIsValid(isValidTimeInput);
    if (isValidTimeInput && !!selectedValue.startDate) {
      const timeInfo = getTimeInfo(value);
      const startDate = setTimeOnDate(selectedValue.startDate, timeInfo.hour24, timeInfo.minute);

      onDatesChange(startDate, selectedValue.endDate, undefined);
    }
  };

  const handleEndTimeChange = (value: string) => {
    const isValidTimeInput = VALID_TIME_INPUT_FORMATS.map((format) => moment(value, format, true).isValid()).some(
      (isValid) => isValid,
    );

    setEndTimeIsValid(isValidTimeInput);
    if (isValidTimeInput && !!selectedValue.endDate) {
      const timeInfo = getTimeInfo(value);

      const endDate = setTimeOnDate(selectedValue.endDate, timeInfo.hour24, timeInfo.minute);

      onDatesChange(selectedValue.startDate, endDate, undefined);
    }
  };

  useEffect(() => {
    if (selectedValue.selectedPreset || (selectedValue.startDate && selectedValue.endDate)) {
      setButtonLabel(getInputLabel({ selectedValue, presets }));
    }
  }, [selectedValue, presets]);

  const hasSelectedRange = useMemo(
    () =>
      selectedValue.startDate &&
      selectedValue.endDate &&
      [selectedValue.startDate, selectedValue.endDate].filter((date) => moment(date).isValid()).length === 2,
    [selectedValue],
  );

  const presetOptions = useMemo(
    () =>
      presets.map((preset) => ({
        label: presetLabel(preset),
        value: presetValue(preset),
        quantity: preset.quantity,
        unit: preset.unit,
      })),
    [presets],
  );

  const handleAbsoluteDateChange = (selectedDates: Date[]) => {
    let startDate = selectedDates[0];
    let endDate = selectedDates[1];
    if (startDate) {
      startDate = moment.max(moment(startDate).startOf('day'), moment(minDate)).toDate();
    }
    if (endDate) {
      endDate = moment.min(moment(endDate).endOf('day'), moment(maxDate)).toDate();
    }

    // When calendar dates are selected, disable live mode
    if ((startDate || endDate) && selectedValue.isLiveMode && onLiveModeChange) {
      onLiveModeChange(false);
    }

    // only update to parent when both are selected
    if (startDate && endDate) {
      handleDatesChange({ startDate, endDate });
    }

    const newDates = [startDate, endDate].filter((date) => !!date);
    setAbsoluteDateRange(newDates);
  };

  const handlePresetSelected = (preset: DateRangePreset) => {
    const end = moment().toDate();
    const start = presetStartDate(preset);

    // When a preset is selected, disable live mode
    if (selectedValue.isLiveMode && onLiveModeChange) {
      onLiveModeChange(false);
    }

    handleDatesChange({
      startDate: start,
      endDate: end,
      selectedPreset: preset,
    });
  };

  const validStartDateTime = startDateIsValid && startTimeIsValid;
  const validEndDateTime = endDateIsValid && endTimeIsValid;

  return (
    <DatePickerStateProvider
      config={{
        selectedDates: absoluteDateRange,
        onDatesChange: handleAbsoluteDateChange,
        dates: {
          mode: 'range',
          minDate,
          maxDate,
          selectSameDate: true,
        },
      }}
    >
      <Menu.Button
        render={<Button size={size} variant={variant} isDisabled={props.disabled} />}
        {...(props as Omit<MenuButtonProps, 'ref'>)}
      >
        {iconLeft}
        <LPText>{buttonLabel}</LPText>
        <IconSolidCheveronDown />
      </Menu.Button>
      <Menu.List>
        {menuState === MenuState.Default && (
          <>
            {showLiveMode && (
              <>
                <Menu.Item
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLiveModeToggle();
                  }}
                >
                  <Stack py="2" direction="row" align="center" gap="8">
                    <CheckboxIconIfSelected isSelected={!!selectedValue.isLiveMode} />
                    <LiveModeIndicator paused={liveModePaused} />
                    <Text userSelect="none">Live mode</Text>
                  </Stack>
                </Menu.Item>
                <Menu.Divider />
              </>
            )}
            {presetOptions.map((preset) => (
              <Menu.Item
                key={preset.label}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePresetSelected(preset);
                }}
              >
                <Stack py="2" direction="row" align="center" gap="4">
                  <CheckboxIconIfSelected
                    isSelected={!selectedValue.isLiveMode && isPresetSelected(preset, selectedValue.selectedPreset)}
                  />
                  <Text userSelect="none">{preset.label}</Text>
                </Stack>
              </Menu.Item>
            ))}
            {noCustom ? null : (
              <Menu.Item
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenuState(MenuState.Custom);
                }}
              >
                <Stack width="full" display="flex" direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" align="center" gap="4">
                    <CheckboxIconIfSelected
                      isSelected={
                        !selectedValue.isLiveMode &&
                        isCustomSelected({
                          presets,
                          selectedValue,
                        })
                      }
                    />
                    <Text userSelect="none">Custom</Text>
                  </Stack>
                  <IconSolidCheveronRight size={16} />
                </Stack>
              </Menu.Item>
            )}
            {defaultPreset && setDefaultPreset && (
              <>
                <Menu.Divider />
                <Menu>
                  <Menu.Button render={<Menu.Item />}>
                    <Stack
                      width="full"
                      display="flex"
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" align="center" gap="4">
                        <IconSolidClock size={16} />
                        <Text userSelect="none">Default</Text>
                        <Badge label={`${defaultPreset.quantity} ${defaultPreset.unit}`} />
                      </Stack>
                      <IconSolidCheveronRight size={16} />
                    </Stack>
                  </Menu.Button>
                  <Menu.List>
                    {presetOptions.map((preset) => (
                      <Menu.Item
                        key={preset.label}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDefaultPreset(preset);
                        }}
                      >
                        <Stack py="2" direction="row" align="center" gap="4">
                          <CheckboxIconIfSelected isSelected={isPresetSelected(preset, defaultPreset)} />
                          <Text userSelect="none">{`${preset.quantity} ${preset.unit}`}</Text>
                        </Stack>
                      </Menu.Item>
                    ))}
                  </Menu.List>
                </Menu>
              </>
            )}
          </>
        )}
        {menuState === MenuState.Custom && (
          <Form>
            <Box borderBottom="divider" pb="4" px="4" display="flex" gap="4">
              <Box style={{ width: 130 }} border={validStartDateTime ? 'secondary' : 'error'} borderRadius="6">
                <Box borderBottom={validStartDateTime ? 'secondary' : 'error'}>
                  <DateInput
                    name="startDate"
                    placeholder={startDatePlaceholder || 'Start date'}
                    onDateChange={handleStartDateInput}
                  />
                </Box>
                <Box>
                  <TimeInput name="startTime" placeholder={startTimePlaceholder} onTimeChange={handleStartTimeChange} />
                </Box>
              </Box>
              <Box style={{ width: 130 }} border={validEndDateTime ? 'secondary' : 'error'} borderRadius="6">
                <Box borderBottom={validEndDateTime ? 'secondary' : 'error'}>
                  <DateInput
                    name="endDate"
                    placeholder={endDatePlaceholder || 'End date'}
                    onDateChange={handleEndDateInput}
                  />
                </Box>
                <Box>
                  <TimeInput name="endTime" placeholder={endTimePlaceholder} onTimeChange={handleEndTimeChange} />
                </Box>
              </Box>
            </Box>

            <Menu.Item
              style={{ padding: 0 }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <DatePicker hasSelectedRange={hasSelectedRange} />
            </Menu.Item>
          </Form>
        )}
      </Menu.List>
    </DatePickerStateProvider>
  );
};
